// Resolve a repository to a launchable working directory on an AHP host: match
// the host's projects catalogue or clone it and wait for the clone to settle.
// The resolved `path` is what a worktree `createSession` binds to. Opt-in: only
// the repo-scoped AHP launch path uses it, after `initialize`, before `createSession`.

import type {AhpClient, Subscription} from '@microsoft/agent-host-protocol/client'
import type {RootConfigChangedAction, RootState, StateAction} from '@microsoft/agent-host-protocol'
import {
  matchProjectByCloneUrl,
  readProjectManagementAvailable,
  readProjectsCatalogue,
  readProjectsFromConfigDelta,
  type ProjectEntry,
} from './project-catalogue'
import {cloneProject} from './project-commands'

const ACTION_ROOT_CONFIG_CHANGED = 'root/configChanged'
const DEFAULT_CLONE_TIMEOUT_MS = 180_000

export type ProvisioningPhase = 'preparing' | 'cloning' | 'ready' | 'failed'

/** A provisioning progress update; `progress` is a 0..100 clone percent. */
export interface ProvisioningStatus {
  phase: ProvisioningPhase
  progress?: number
  error?: string
}

export type ProvisioningFailureReason = 'unsupported' | 'clone-failed' | 'timeout' | 'closed'

/** Raised when a repo cannot be resolved to a launchable project. */
export class ProjectProvisioningError extends Error {
  readonly reason: ProvisioningFailureReason
  constructor(reason: ProvisioningFailureReason, message: string) {
    super(message)
    this.name = 'ProjectProvisioningError'
    this.reason = reason
  }
}

export interface ResolveProjectOptions {
  client: AhpClient
  rootState: RootState | undefined
  /** Clone URL used to fetch the repo; also the host-aware catalogue match key. */
  cloneUrl: string
  /** Branch to check out when cloning a project that is not already catalogued. */
  cloneBranch?: string
  /** Attaches a subscription to the already-subscribed root channel. */
  watchRoot: () => Subscription
  /** Ensures the clone credential is authenticated before a private clone. */
  authenticate?: () => Promise<void>
  onProgress?: (status: ProvisioningStatus) => void
  timeoutMs?: number
}

export interface ResolvedProject {
  path: string
  defaultBranch?: string
}

function isReady(entry: ProjectEntry): boolean {
  return entry.status === 'ready' && !!entry.path
}

function toResolved(entry: ProjectEntry): ResolvedProject {
  return {path: entry.path, ...(entry.defaultBranch ? {defaultBranch: entry.defaultBranch} : {})}
}

function isRootConfigChanged(action: StateAction): action is RootConfigChangedAction {
  return action.type === ACTION_ROOT_CONFIG_CHANGED
}

/**
 * Resolve a repository to a launchable working directory. Throws
 * `ProjectProvisioningError('unsupported')` when the host lacks project
 * management; returns immediately for an already-`ready` project; otherwise
 * authenticates, clones, and watches `root/configChanged` until the entry
 * settles to `ready` (resolve) or `failed` (throw).
 */
export async function resolveProjectWorkingDirectory({
  client,
  rootState,
  cloneUrl,
  cloneBranch,
  watchRoot,
  authenticate,
  onProgress,
  timeoutMs = DEFAULT_CLONE_TIMEOUT_MS,
}: ResolveProjectOptions): Promise<ResolvedProject> {
  if (!readProjectManagementAvailable(rootState)) {
    throw new ProjectProvisioningError(
      'unsupported',
      'This environment does not support repository projects (dynamic project management is disabled).',
    )
  }

  onProgress?.({phase: 'preparing'})

  const existing = matchProjectByCloneUrl(readProjectsCatalogue(rootState), cloneUrl)
  if (existing) {
    if (existing.status === 'failed') {
      // Surface a prior terminal `failed` entry immediately rather than silently
      // re-cloning, which could mask a persistently failing repo.
      onProgress?.({phase: 'failed', error: existing.error})
      throw new ProjectProvisioningError(
        'clone-failed',
        existing.error ?? 'A previous clone of this repository failed on the host.',
      )
    }
    if (isReady(existing)) {
      onProgress?.({phase: 'ready'})
      return toResolved(existing)
    }
  }

  // Attach the root watcher before cloning so no clone-advancing delta is missed.
  const subscription = watchRoot()
  try {
    let targetId = existing?.id
    if (!existing) {
      await authenticate?.()
      onProgress?.({phase: 'cloning', progress: 0})
      const provisional = await cloneProject(client, {
        url: cloneUrl,
        ...(cloneBranch ? {branch: cloneBranch} : {}),
        depth: 1,
      })
      targetId = provisional.id
      if (isReady(provisional)) {
        onProgress?.({phase: 'ready'})
        return toResolved(provisional)
      }
      onProgress?.({phase: 'cloning', progress: provisional.progress ?? 0})
    } else {
      onProgress?.({phase: 'cloning', progress: existing.progress ?? 0})
    }

    return await watchUntilReady({subscription, targetId, cloneUrl, onProgress, timeoutMs})
  } finally {
    void subscription.close()
  }
}

interface WatchOptions {
  subscription: Subscription
  targetId: string | undefined
  cloneUrl: string
  onProgress?: (status: ProvisioningStatus) => void
  timeoutMs: number
}

/** Find the entry we are provisioning within a fresh catalogue snapshot. */
function selectEntry(
  projects: ProjectEntry[],
  targetId: string | undefined,
  cloneUrl: string,
): ProjectEntry | undefined {
  if (targetId) {
    const byId = projects.find(entry => entry.id === targetId)
    if (byId) return byId
  }
  return matchProjectByCloneUrl(projects, cloneUrl)
}

/**
 * Consume root-config deltas until the tracked project settles. A real timer is
 * raced against `next()` so a host that stalls mid-clone still times out; a
 * stream close before settling is surfaced as a retryable `closed` failure.
 */
async function watchUntilReady({
  subscription,
  targetId,
  cloneUrl,
  onProgress,
  timeoutMs,
}: WatchOptions): Promise<ResolvedProject> {
  const iterator = subscription[Symbol.asyncIterator]()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<{kind: 'timeout'}>(resolve => {
    timer = setTimeout(() => resolve({kind: 'timeout'}), timeoutMs)
  })
  const nextEvent = async () => ({kind: 'event' as const, result: await iterator.next()})

  try {
    for (;;) {
      const next = await Promise.race([nextEvent(), timedOut])

      if (next.kind === 'timeout') {
        onProgress?.({phase: 'failed', error: 'timed out'})
        throw new ProjectProvisioningError('timeout', `The repository clone did not finish within ${timeoutMs}ms.`)
      }

      const {result} = next
      if (result.done) {
        // The root subscription dropped before settling — retryable; the runtime
        // re-establishes and re-drives provisioning.
        throw new ProjectProvisioningError(
          'closed',
          'The connection closed before the repository finished provisioning.',
        )
      }

      const evt = result.value
      if (evt.type !== 'action') continue
      const action = evt.params.action
      if (!isRootConfigChanged(action)) continue

      const projects = readProjectsFromConfigDelta(action.config)
      if (!projects) continue

      const entry = selectEntry(projects, targetId, cloneUrl)
      if (!entry) continue

      if (entry.status === 'failed') {
        onProgress?.({phase: 'failed', error: entry.error})
        throw new ProjectProvisioningError('clone-failed', entry.error ?? 'The repository clone failed on the host.')
      }
      if (isReady(entry)) {
        onProgress?.({phase: 'ready'})
        return toResolved(entry)
      }
      onProgress?.({phase: 'cloning', progress: entry.progress ?? 0})
    }
  } finally {
    if (timer) clearTimeout(timer)
  }
}
